import { PlayLayout } from '@app/play-now/routes/PlayLayout';
import { getRedirectRoute, setupPlayUtils } from '@app/play-now/routes/remix-route-helper';
import { ClientMetaFunction, mergeMeta } from '@app/utils/remix/play-now-meta';
import { loadTvProgramGuide } from '@play-now/video/api/load-tv-program-guide';
import { loadTvProgramGuideNonSrg } from '@play-now/video/api/load-tv-program-guide-non-srg';
import { getRemixMeta } from '@play-now/video/components/Meta/PlayMetaData';
import { queryKeys } from '@play-now/video/config/use-query';
import { useInitialData } from '@play-now/video/config/VideoAppContext';
import { TvGuideChannelPage } from '@play-now/video/pages/TvGuideChannelPage/TvGuideChannelPage';
import { tvGuideByChannelPath } from '@play-now/video/pages/TvGuideChannelPage/TvGuideChannelPage.route';
import { getValidDate } from '@play-now/video/pages/TvGuidePage/tv-guide-helpers';
import { VideoInitialData } from '@play-now/video/routes/Routes';
import { shortDateFormat, isoDate as toIsoDate } from '@play-now/video/utils/date-formatter';
import { LoaderFunctionArgs } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';

export const loader = async (args: LoaderFunctionArgs) => {
  const { config, axios, params, query, i18n } = setupPlayUtils(args);
  const dateString = toIsoDate(getValidDate(params.isoDate));
  const providers = config.tvGuide?.tvGuideProviders ?? [];

  const results = await Promise.all(
    providers.map((provider) =>
      provider === 'non-srg' ? loadTvProgramGuideNonSrg(dateString, axios, true) : loadTvProgramGuide(dateString, axios, [provider], true)
    )
  );
  const tvGuide = providers.reduce(
    (acc, cur, index) => ({
      ...acc,
      [queryKeys.TvProgramGuide(dateString, cur)]: results[index],
    }),
    {} as VideoInitialData['tvGuide']
  );

  const channels = tvGuide?.[dateString]?.map((program) => program.channel);
  const channel = channels?.find((chan) => chan.urn === query.channelUrn) || channels?.[0];
  if (channel) {
    const redirectResponse = getRedirectRoute({
      fullPath: config.baseUrl + tvGuideByChannelPath(i18n, dateString, channel),
      url: args.request.url,
    });
    if (redirectResponse) return redirectResponse;
  }

  const metaData = getRemixMeta({
    config,
    title: i18n.t('global.tvGuideByChannel'),
    description: i18n.t('missedShow.metaDescription', {
      date: shortDateFormat(dateString),
    }),
  });

  return { tvGuide, meta: metaData };
};

export const meta: ClientMetaFunction<typeof loader> = ({ data, matches }) => mergeMeta({ data, matches });

const TvGuideChannelPageRoute = () => {
  const { tvGuide } = useLoaderData<typeof loader>();
  const initialData = useInitialData();
  return (
    <PlayLayout pageId='tvGuideByChannel'>
      <TvGuideChannelPage initialData={{ ...initialData, tvGuide }} />
    </PlayLayout>
  );
};

export default TvGuideChannelPageRoute;
